import { Link } from 'gatsby';
import { InspirationalCard } from 'libs/brand-system/src/components/InspirationalCard';
import { ValueCard } from 'libs/brand-system/src/components/ValueCard';
import { ButtonCard } from 'libs/growth-platform-brand-system-v2/src/components/ButtonCard';
import { ButtonV2 } from 'libs/growth-platform-brand-system-v2/src/components/ButtonV2';
import { NavigationButton } from 'libs/growth-platform-brand-system-v2/src/components/NavigationButton';
import { Picto } from 'libs/growth-platform-brand-system-v2/src/components/Picto';
import ChevronRightIcon from 'libs/growth-platform-brand-system-v2/src/foundations/pictograms/chevron.svg';
import { BlogCardLayout } from 'libs/growth-platform-brand-system-v2/src/layouts/BlogCardLayout';
import { CardWithoutBg } from 'libs/growth-platform-brand-system-v2/src/layouts/CardWithoutBg';
import { classNames } from 'libs/growth-platform-brand-system-v2/src/utils/style';
import { ButtonTracking } from 'libs/growth-platform-brand-system-v2/src/utils/tracking/ButtonTracking';
import { morphism } from 'morphism';
import React, { Fragment } from 'react';
import { schemaBlogCardLayout } from '../morphismSchema/layouts/schemaBlogCardLayout';
import { schemaCardWithoutBg } from '../morphismSchema/layouts/schemaCardWithoutBg';
import { schemaCardButtonLink } from '../morphismSchema/schemaCardButtonLinkProps';
import { schemaDatoButton } from '../morphismSchema/schemaDatoButton';
import { readArticle } from '../templates/subcategory-page';
import { linkResolver } from './linkResolver';
import { TitleWithArrow } from 'libs/growth-platform-brand-system-v2/src/components/ButtonV2/TitleWithArrow';

export const getCardsListCards = ({
  cards,
  screenSize,
  locale,
  section,
}: any) => {
  const cardsList = cards?.map((card: any, i: number) => {
    let type = card?.__typename;
    let ctaBtn = card?.ctaButton || card?.button;
    const isBlogArticle = card?.metaFields;
    const typeName =
      card?.ctaButton?.[0]?.internalUrl?.__typename ||
      card?.ctaButton?.internalUrl?.__typename;
    const isClientPage = typeName === 'DatoCmsClientPage';

    if (Array.isArray(ctaBtn)) ctaBtn = ctaBtn?.[0];

    let cardButtonLinkProps = {
      ctaButton: { ...ctaBtn, locale },
      screenSize,
      isBlogArticle: false,
      isClientLink: false,
      locale,
    };

    if (isClientPage) {
      type = 'DatoCmsCardWithoutBackground';
      cardButtonLinkProps = {
        ...cardButtonLinkProps,
        ctaButton: {
          internalUrl: {
            slug: card.slug || ctaBtn.internalUrl?.slug,
          },
          locale,
          as: 'span',
          title: readArticle[locale],
        },
        isClientLink: true,
      };
    }

    if (isBlogArticle) {
      type = 'DatoCmsBlogCard';
      cardButtonLinkProps = {
        ...cardButtonLinkProps,
        ctaButton: {
          internalUrl: {
            slug: card.slug,
          },
          locale,
          as: 'span',
          title: readArticle[locale],
        },
        isBlogArticle: true,
      };
    }

    const { button, ...linkProps } = morphism(schemaCardButtonLink, {
      ...cardButtonLinkProps,
    });
    const { component, ...btnProps } = button.props;

    const cardsProps = card?.metaFields
      ? { ...card, ...card?.metaFields }
      : { ...card };

    const { ctaButton, ...cardProps } = {
      ...morphism(schemaCardWithoutBg, {
        ...cardsProps,
        ...linkProps,
        locale,
        ctaButton: btnProps,
      }),
    };

    if (type === 'DatoCmsInspirationalCard') {
      const InspirationalCardProps = {
        ...{
          ...card,
          as: linkProps?.as,
          image: card?.image,
          capsText: card?.capsText,
          className: 'h-full col-span-1 lg:col-span-2',
          tags: card?.tags.map((t: any) => ({
            ...t,
            size: 'small',
          })),
          contentPosition: 'left',
          linkProps: {
            ...linkProps,
            button: btnProps ? (
              <ButtonV2
                {...{
                  ...btnProps,
                  className: 'text-21',
                  title: <TitleWithArrow title={btnProps.title} />,
                }}
              />
            ) : undefined,
          },
        },
      };

      const { __typename, logoUrl, ctaButton, ...inspirationalCardProps } =
        InspirationalCardProps;
      return (
        <ButtonTracking
          section={section}
          index={i}
          component={InspirationalCard}
          key={i}
          to={linkProps?.linkAttributes?.to}
          href={linkProps?.linkAttributes?.href}
          {...inspirationalCardProps}
          labelTracking={cardProps.title}
        />
      );
    }
    if (type === 'DatoCmsBlogCard') {
      const cardProps = morphism(schemaBlogCardLayout, {
        ...card,
        ...linkProps,
      });
      return (
        <ButtonTracking
          component={BlogCardLayout}
          {...{
            ...cardProps,
            ctaButton: ctaButton,
            imgPosition: isBlogArticle ? 'none' : 'top',
            className: 'w-full',
            displaySubtitleOnSmallDevice: true,
          }}
          to={linkProps?.linkAttributes?.to}
          href={linkProps?.linkAttributes?.href}
          section={section}
          index={i}
          key={i}
          labelTracking={cardProps?.title}
        />
      );
    }
    if (type === 'DatoCmsCardWithoutBackground') {
      return (
        <ButtonTracking
          component={CardWithoutBg}
          {...{
            ...cardProps,
            ctaButton,
            ...linkProps?.linkAttributes,
            imgPosition: 'top',
            className: 'w-full sm:mx-24 md:mx-0 mb-[30px]',
          }}
          section={section}
          index={i}
          key={i}
          labelTracking={cardProps?.title}
        />
      );
    }
    if (type === 'DatoCmsNbButtonCard') {
      const { as, linkAttributes } = linkProps;
      const { image, content, ..._cardProps } = cardProps;
      return (
        <ButtonTracking
          component={ButtonCard}
          key={i}
          section={section}
          index={i}
          {...{
            image,
            ...linkAttributes,
            as: as,
            className: 'group-hover:border-opacity-0',
            button: btnProps && (
              <ButtonV2 {..._cardProps} title={btnProps?.title} />
            ),
          }}
          labelTracking={btnProps?.title}
        />
      );
    }
    if (type === 'DatoCmsNbValueCard')
      return (
        <ButtonTracking
          component={ValueCard}
          key={i}
          section={section}
          index={i}
          {...{ ...card, illustration: card?.image }}
          labelTracking={cardProps?.title}
        />
      );
    return <Fragment key={i}></Fragment>;
  });

  return cardsList;
};

export const getTextSectionContent = (block: any, locale: string) => {
  return block?.textColumnsItems?.map((item: any) => {
    let button = item?.ctaButton?.[0];
    if (button) button = morphism(schemaDatoButton, { ...button, locale });
    return {
      ...item,
      button: button ? (
        <ButtonTracking
          component={ButtonV2}
          {...button}
          title={<TitleWithArrow title={button.title} />}
          color="invert"
          labelTracking={button.title}
        />
      ) : (
        []
      ),
    };
  });
};

export const getButtons = (block: any, locale: string, sectionId?: string) => {
  return block?.ctas?.map((cta: any, i: number) => {
    const color =
      block?.isDark && !['default', 'invert'].includes(cta?.color)
        ? 'invert'
        : cta?.color;

    const btnProps = morphism(schemaDatoButton, {
      ...cta,
      locale,
      color,
      className:
        'w-full md:w-auto' + (cta.type !== 'minimal' ? ' mb-16 lg:mr-16' : ''),
    });
    return (
      <ButtonTracking
        component={ButtonV2}
        section={sectionId}
        index={i}
        {...btnProps}
        labelTracking={btnProps.title}
        key={i}
        title={
          cta.type === 'minimal' ? (
            <TitleWithArrow title={cta?.title} />
          ) : (
            btnProps.title
          )
        }
      />
    );
  });
};

export const NavigationButtons = ({
  navigationButtons,
  withBorder = false,
  sectionId,
  locale,
}: {
  navigationButtons: any[];
  variant?: 'light' | 'dark';
  withBorder?: boolean;
  sectionId?: string;
  locale: string;
}) => {
  return (
    <>
      {navigationButtons.reduce((acc, { icon, ...item }, key) => {
        const _icon = icon?.name ? icon : { name: icon, size: 'big' };
        if (item.link)
          acc.push(
            <div
              className="w-full justify-start p-12 md:w-1/2 lg:w-4/12"
              key={key}
            >
              <ButtonTracking
                component={NavigationButton}
                section={sectionId}
                labelTracking={item.title}
                index={key}
                to={linkResolver(item.link, locale)}
                as={Link}
                className={classNames(
                  withBorder ? 'border-neutral-51' : 'border-transparent',
                )}
              >
                <Picto {..._icon} />
                <div className="flex flex-1 justify-between">
                  {item.title}
                  <ChevronRightIcon className="ml-8 h-20 rotate-90 transform" />
                </div>
              </ButtonTracking>
            </div>,
          );
        return acc;
      }, [])}
    </>
  );
};
